import { usGameSportForKey } from '@/lib/polymarketUs/sports';
/**
 * Polymarket US as a venue of the shared Game Detail panel (#987).
 *
 * Resolves, for one board game: the caller's visible US accounts (read once per
 * login epoch), the selected account's scope/permissions/readiness, the game's
 * single US moneyline (exact producer game key + YES team), and live depth for
 * that market while the panel is showing US. Everything here is read-only; the
 * order ticket owns its own trading client. Every unavailable state is explicit,
 * so the panel can say why US is unavailable instead of hiding the venue.
 *
 * Without a visible US account the market and depth still resolve, through the
 * account-free public reads (as Kalshi/Polymarket prices show without keys);
 * the state then carries `noAccount: true` and no account, context or scope, so
 * nothing downstream can mount an order ticket from it.
 *
 * Every result is stored with the key it was read for (epoch, account, game,
 * market) and only used while that key still matches, so a late or previous
 * reply can never paint into a different account, game or login.
 *
 * #1006 — a caller acting for one exact account (a Tracker row) names it in
 * `requestedAccountId`. That account is selected, or, when it is not among
 * the caller's visible own-view accounts, the state is `account-unavailable`:
 * nothing is read or shown for any other account until the trader picks one.
 * A trader's pick lasts until the next request; any new request clears it.
 * `requestedMarketSlug` (the row's market) narrows the moneyline to exactly
 * that market: another one listed for the game is never joined in its place.
 *
 * #1030 — a full-game total line reads the game's `totals` catalogue (bounded
 * by `US_TOTAL_MAX_PAGES`; an incomplete read is an error, not a pick) and
 * joins only the one native market of its family at its exact threshold.
 * A refresh that cannot confirm the whole set keeps the last good market on
 * screen with `catalogueUnconfirmed` set (the host pauses new orders on it)
 * and retries after `US_TOTAL_REFRESH_RETRY_MS`; the next complete read either
 * confirms the market or removes it by the same rules as a first read.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { listVisibleUsAccounts, type UsAccountClient, type UsAccountContext, type UsVisibleAccount } from '@/lib/polymarketUs/accountClient';
import type { UsDiscoveryMarket, UsMarketBookData, UsMarketClient } from '@/lib/polymarketUs/marketClient';
import { UsClientError } from '@/lib/polymarketUs/tradingClient';
import { pickUsMoneyline, pickUsSpread, pickUsPlayerProp, pickUsGameProp, usGameKeyForBoardKey, type UsLineTarget } from '@/lib/polymarketUs/gameMapping';
import { pickUsFullGameTotal } from '@/lib/polymarketUs/totalTarget';

export interface UsGameVenueClients {
  accounts: UsAccountClient;
  /** Catalogue reads for this game. */
  discovery: UsMarketClient;
  /** Dedicated instance: each read supersedes earlier reads on one client. */
  books: UsMarketClient;
}

interface UsAccountResolved {
  noAccount: false;
  accounts: UsVisibleAccount[];
  accountId: string;
  context: UsAccountContext;
}
/** No visible US account: market evidence is display-only; nothing is tradable. */
interface UsNoAccount {
  noAccount: true;
}
export type UsVenueAccess = UsAccountResolved | UsNoAccount;

export type UsGameVenueState =
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'error'; code: string; noAccount: boolean }
  /** The requested account is not one the caller may use here; no other is substituted. */
  | { status: 'account-unavailable'; requestedAccountId: string; accounts: UsVisibleAccount[] }
  | ({ status: 'no-market'; missingMarketSlug?: string } & UsVenueAccess)
  | ({ status: 'ambiguous'; count: number } & UsVenueAccess)
  | ({ status: 'ready'; market: UsDiscoveryMarket; book: UsMarketBookData | null; bookError: string | null;
      /** #1030 — a total only: the code of the latest catalogue refresh, which
       * failed, so `market` comes from an earlier complete read the hook can no
       * longer vouch for. New orders wait for a complete read. Absent or null
       * when the latest read was complete. */
      catalogueUnconfirmed?: string | null }
      & UsVenueAccess);

export interface UsGameVenueInput {
  boardKey: string;
  awayCode: string | null | undefined;
  homeCode: string | null | undefined;
  /** Period winners are team binaries (a draw can exist): never substitute
   * the other team's NO as this team's YES. Full-game mapping is unchanged. */
  selectedTeam?: string | null;
  /** Load at all (panel open on this game). */
  enabled: boolean;
  /** Poll depth (US is the visible venue and the page is visible). */
  live: boolean;
  /** Login epoch: a replacement login discards every account-derived value. */
  sessionEpoch: number;
  /** The exact board line (default moneyline). `unsupported` never joins a market. */
  line?: UsLineTarget;
  /** #1006 — act for this account only: never fall back to another one. */
  requestedAccountId?: string | null;
  /** Stamps each request, so repeating one re-applies it over a manual pick. */
  accountRequestSeq?: number;
  /** #1006 — the exact moneyline the caller acts on (a Tracker row's market). */
  requestedMarketSlug?: string | null;
}

export const US_BOOK_POLL_MS = 1_000;
export const US_DISCOVERY_REFRESH_MS = 30_000;
/** R4-F5 — the account list and the selected account context are read again
 * when the window regains focus or becomes visible, at most this often, so a
 * mid-session change by a manager (a disabled account, a withdrawn grant, a
 * key change) reaches the panel before the trader acts on it. */
export const US_ACCOUNTS_REREAD_MIN_MS = 30_000;
const MAX_PAGES = 3;
/** #1030 — one game's totals catalogue (every listed strike, plus period
 * totals the picker refuses) is read whole or not at all: a cursor still open
 * after this bound is an incomplete candidate set, never a partial pick. */
export const US_TOTAL_MAX_PAGES = 10;
/** #1030 — after a failed totals read, the next reads come this soon (then
 * the normal refresh interval), so a brief catalogue outage pauses orders for
 * seconds rather than a whole refresh interval. Reset by a complete read. */
export const US_TOTAL_REFRESH_RETRY_MS = [2_000, 5_000] as const;
const MONEYLINE: UsLineTarget = { kind: 'moneyline' };
const codeOf = (err: unknown) => (err instanceof UsClientError ? err.code : 'US_MARKET_UNAVAILABLE');
const superseded = (err: unknown) => err instanceof UsClientError && err.code === 'US_CLIENT_CONTEXT_CHANGED';

type Keyed<T> = { key: string; value: T | null; error: string | null };
const empty = <T,>(): Keyed<T> => ({ key: '', value: null, error: null });
const read = <T,>(state: Keyed<T>, key: string | null) =>
  key !== null && state.key === key ? state : { key: '', value: null, error: null };

export function useUsGameVenue(
  clients: UsGameVenueClients,
  input: UsGameVenueInput,
): UsGameVenueState & { selectAccount(accountId: string): void } {
  const usKey = useMemo(() => usGameKeyForBoardKey(input.boardKey), [input.boardKey]);
  const sport = usGameSportForKey(usKey);
  const active = input.enabled && usKey !== null && sport !== null && !!input.awayCode && !!input.homeCode;
  const epoch = String(input.sessionEpoch);

  const [accountsState, setAccountsState] = useState<Keyed<UsVisibleAccount[]>>(empty);
  const [chosen, setChosen] = useState<{ epoch: string; accountId: string } | null>(null);
  const [contextState, setContextState] = useState<Keyed<UsAccountContext>>(empty);
  const [marketsState, setMarketsState] = useState<Keyed<UsDiscoveryMarket[]>>(empty);
  const [bookState, setBookState] = useState<Keyed<UsMarketBookData>>(empty);

  // 1. Visible accounts with an own view, once per login epoch, and again on
  // focus/visibility (bounded by US_ACCOUNTS_REREAD_MIN_MS; R4-F5).
  const [accountsReread, setAccountsReread] = useState(0);
  // Counts list reads that settled: the context is read after each one, never
  // beside it (one account client supersedes an earlier read with a later one).
  const [listSettled, setListSettled] = useState(0);
  const lastAccountsRead = useRef<{ epoch: string; atMs: number } | null>(null);
  const attemptedRead = useRef<string | null>(null);
  const rereadKey = `${epoch}|${accountsReread}`;
  const accountsLoaded = accountsState.key === epoch && (accountsState.value !== null || accountsState.error !== null);
  useEffect(() => {
    if (!active || (accountsLoaded && attemptedRead.current === rereadKey)) return;
    attemptedRead.current = rereadKey;
    lastAccountsRead.current = { epoch, atMs: performance.now() };
    let cancelled = false;
    (async () => {
      const rows = await listVisibleUsAccounts(clients.accounts, MAX_PAGES);
      if (cancelled) return;
      const value = rows.filter((a) => a.canViewOwn);
      // An unchanged list is not a new state: nothing downstream re-reads.
      setAccountsState((prev) => (prev.key === epoch && prev.value && JSON.stringify(prev.value) === JSON.stringify(value)
        ? prev : { key: epoch, value, error: null }));
      setListSettled((n) => n + 1);
    })().catch((err) => {
      if (cancelled || superseded(err)) return;
      // A failed re-read keeps the list in hand; a failed first read is the error it was.
      setAccountsState((prev) => (prev.key === epoch && prev.value ? prev : { key: epoch, value: null, error: codeOf(err) }));
      setListSettled((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [active, accountsLoaded, epoch, rereadKey, clients.accounts]);
  useEffect(() => {
    if (!active) return;
    const onFocus = () => {
      if (document.hidden) return;
      const last = lastAccountsRead.current;
      if (last && last.epoch === epoch && performance.now() - last.atMs < US_ACCOUNTS_REREAD_MIN_MS) return;
      setAccountsReread((n) => n + 1);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [active, epoch]);

  const accountsRead = read(accountsState, epoch);
  const accounts = accountsRead.value;
  const requested = input.requestedAccountId ?? null;
  const request = requested ? `${requested}|${input.accountRequestSeq ?? 0}` : null;
  // Any new request clears the trader's pick, so an older pick can never come
  // back when a request repeats an earlier one (React's reset-on-prop-change).
  const [pickRequest, setPickRequest] = useState(request);
  if (pickRequest !== request) {
    setPickRequest(request);
    setChosen(null);
  }
  const visible = (id: string | null) => !!id && !!accounts?.some((a) => a.accountId === id);
  const chosenId = chosen?.epoch === epoch && pickRequest === request ? chosen.accountId : null;
  const accountId = visible(chosenId) ? chosenId
    : requested ? (visible(requested) ? requested : null)
    : (accounts?.[0]?.accountId ?? null);
  // Accounts read, and the one asked for is not usable here.
  const requestRefused = accounts !== null && requested !== null && accountId === null;
  const contextKey = accountId ? `${epoch}|${accountId}` : null;
  // Read again with each list re-read (R4-F5); stored under contextKey, so an
  // unchanged context is the same state and nothing downstream restarts.
  const contextReadKey = contextKey ? `${contextKey}|${listSettled}` : null;

  // 2. The selected account's own-view scope, permissions and readiness.
  useEffect(() => {
    if (!active || !accountId || !contextKey || !contextReadKey) return;
    let cancelled = false;
    clients.accounts
      .selectAccount(accountId, 'own')
      .then((ctx) => {
        if (cancelled) return;
        setContextState((prev) => (prev.key === contextKey && prev.value && JSON.stringify(prev.value) === JSON.stringify(ctx)
          ? prev : { key: contextKey, value: ctx, error: null }));
      })
      .catch((err) => {
        if (!cancelled && !superseded(err)) setContextState({ key: contextKey, value: null, error: codeOf(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [active, accountId, contextKey, contextReadKey, clients.accounts]);

  const contextRead = read(contextState, contextKey);
  const context = contextRead.value;
  // Accounts read and none visible: market evidence comes from the public reads.
  // A refused request is not "no account": nothing is read in its place.
  const noAccount = accounts !== null && accountId === null && !requestRefused;
  const readerKey = context ? contextKey : noAccount ? `${epoch}|public` : null;
  const marketsKey = readerKey && usKey ? `${readerKey}|${usKey}` : null;
  // Exact props are fetched by slug, so adding hundreds of props cannot
  // hide a selected contract behind the legacy three-page game bound.
  const playerSlug = (input.line?.kind === 'player-prop' || input.line?.kind === 'game-prop') ? input.line.target.marketSlug : null;
  const catalogueType = playerSlug || input.line?.kind === 'moneyline' && input.line.period && input.line.period !== 'full'
    ? 'props' : input.line?.kind === 'spread' ? 'spreads' : input.line?.kind === 'total' ? 'totals' : 'moneyline';
  const catalogueSlug = playerSlug ?? input.requestedMarketSlug ?? null;
  const catalogueKey = marketsKey ? `${marketsKey}|${catalogueType}|${catalogueSlug ?? 'game-lines'}` : null;

  // 3. This game's US markets (bounded: one game's rows, at most three pages).
  useEffect(() => {
    if (!active || (!context && !noAccount) || !usKey || !sport || !marketsKey) return;
    let cancelled = false;
    let pending = false;
    let failures = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const readCatalogue = async () => {
      if (context) clients.discovery.setSelection({ accountId: context.accountId, scope: context.scope });
      const rows: UsDiscoveryMarket[] = [];
      let after: string | undefined;
      let generation: string | undefined;
      const pageLimit = catalogueType === 'props' && !catalogueSlug ? 20 : catalogueType === 'totals' ? US_TOTAL_MAX_PAGES : MAX_PAGES;
      for (let page = 0; page < pageLimit; page++) {
        const query = { q: catalogueSlug ?? usKey, sport, marketType: catalogueType, ...(after ? { after } : {}) };
        const next = context ? await clients.discovery.listMarkets(query) : await clients.discovery.listPublicMarkets(query);
        if (generation !== undefined && generation !== next.generation) throw new UsClientError('US_CATALOGUE_CHANGED');
        generation = next.generation;
        rows.push(...next.markets.filter((m) => m.gameKey === usKey));
        if (!next.nextCursor) { after = undefined; break; }
        if (after === next.nextCursor) throw new UsClientError('US_CLIENT_INVALID_RESPONSE');
        after = next.nextCursor;
      }
      if ((catalogueType === 'props' || catalogueType === 'totals') && after) throw new UsClientError('US_CATALOGUE_UNAVAILABLE');
      if (!cancelled) setMarketsState({ key: catalogueKey!, value: rows, error: null });
    };
    const onError = (err: unknown) => {
      if (cancelled || superseded(err)) return;
      // Keep the last good list for this exact key; record the failure beside it.
      // #1030 — for totals the failure is surfaced (`catalogueUnconfirmed`): the
      // kept list may no longer be the whole candidate set (a duplicate or a
      // relisted strike since), so the market stays on screen, its ticket and
      // draft with it, while new orders wait for a complete read. That read is
      // tried again shortly, not a whole refresh interval later.
      setMarketsState((prev) => ({ key: catalogueKey!, value: prev.key === catalogueKey ? prev.value : null, error: codeOf(err) }));
      if (catalogueType === 'totals' && failures < US_TOTAL_REFRESH_RETRY_MS.length) {
        clearTimeout(retry);
        retry = setTimeout(load, US_TOTAL_REFRESH_RETRY_MS[failures++]);
      }
    };
    function load() {
      if (cancelled || pending) return;
      pending = true;
      readCatalogue().then(() => { failures = 0; }, onError).finally(() => { pending = false; });
    }
    load();
    const id = setInterval(load, US_DISCOVERY_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(retry);
      clients.discovery.invalidate();
    };
  }, [active, context, noAccount, usKey, marketsKey, catalogueKey, catalogueType, catalogueSlug, sport, clients.discovery]);

  const marketsRead = read(marketsState, catalogueKey);
  const markets = marketsRead.value;
  const line = input.line ?? MONEYLINE;
  const requestedSlug = input.requestedMarketSlug ?? null;
  const lineKind = line.kind;
  const favoredCode = line.kind === 'spread' ? line.favoredCode : null;
  const otherCode = line.kind === 'spread' ? line.otherCode : null;
  const magnitude = line.kind === 'spread' ? line.magnitude : null;
  const totalFamily = line.kind === 'total' ? line.family : null;
  const totalThreshold = line.kind === 'total' ? line.threshold : null;
  const period = line.kind === 'moneyline' ? line.period ?? 'full' : 'full';
  const gamePropTarget = line.kind === 'game-prop' ? line.target : null;
  const playerTarget = line.kind === 'player-prop' ? line.target : null;
  const pick = useMemo(() => {
    if (!markets || !usKey) return null;
    if (lineKind === 'unsupported') return { kind: 'none' as const };
    if (lineKind === 'game-prop') {
      if (!gamePropTarget || requestedSlug && requestedSlug !== gamePropTarget.marketSlug) return { kind: 'none' as const };
      return pickUsGameProp(markets, usKey, gamePropTarget);
    }
    if (lineKind === 'player-prop') {
      if (!playerTarget || requestedSlug && requestedSlug !== playerTarget.marketSlug) return { kind: 'none' as const };
      return pickUsPlayerProp(markets, usKey, playerTarget);
    }
    if (lineKind === 'spread') {
      return pickUsSpread(markets, usKey, { favoredCode: favoredCode!, otherCode: otherCode!, magnitude: magnitude! });
    }
    if (lineKind === 'total') {
      // A saved row's exact market only (the Tracker), never another strike.
      const pool = requestedSlug ? markets.filter((m) => m.marketSlug === requestedSlug) : markets;
      return pickUsFullGameTotal(pool, usKey, { family: totalFamily!, threshold: totalThreshold! });
    }
    // A row's exact market: only it may be joined, never another listed moneyline.
    const pool = requestedSlug ? markets.filter((m) => m.marketSlug === requestedSlug) : markets;
    return pickUsMoneyline(pool, usKey, input.awayCode, input.homeCode, period, input.selectedTeam);
  }, [markets, usKey, lineKind, favoredCode, otherCode, magnitude, totalFamily, totalThreshold, period, playerTarget, gamePropTarget, input.awayCode, input.homeCode, input.selectedTeam, requestedSlug]);
  const slug = pick?.kind === 'market' ? pick.market.marketSlug : null;
  const bookKey = slug && readerKey ? `${readerKey}|${slug}` : null;

  // 4. Live depth for the one selected market, only while US is on screen.
  useEffect(() => {
    if (!active || !input.live || (!context && !noAccount) || !slug || !bookKey) return;
    let cancelled = false;
    if (context) clients.books.setSelection({ accountId: context.accountId, scope: context.scope });
    const readBook = () =>
      (context ? clients.books.readBook(slug) : clients.books.readPublicBook(slug))
        .then((view: UsMarketBookData) => {
          if (!cancelled) setBookState({ key: bookKey, value: view, error: null });
        })
        .catch((err) => {
          // Never keep painting an old ladder as if it were current.
          if (!cancelled && !superseded(err)) setBookState({ key: bookKey, value: null, error: codeOf(err) });
        });
    void readBook();
    const id = setInterval(() => void readBook(), US_BOOK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      clients.books.invalidate();
    };
  }, [active, input.live, context, noAccount, slug, bookKey, clients.books]);

  const bookRead = read(bookState, bookKey);
  const selectAccount = (id: string) => setChosen({ epoch, accountId: id });

  if (!active) return { status: 'off', selectAccount };
  if (accountsRead.error) return { status: 'error', code: accountsRead.error, noAccount: false, selectAccount };
  if (!accounts) return { status: 'loading', selectAccount };
  if (requestRefused && requested) return { status: 'account-unavailable', requestedAccountId: requested, accounts, selectAccount };
  if (accountId && contextRead.error) return { status: 'error', code: contextRead.error, noAccount: false, selectAccount };
  if (accountId && !context) return { status: 'loading', selectAccount };
  const resolved: UsVenueAccess = accountId && context
    ? { noAccount: false, accounts, accountId, context }
    : { noAccount: true };
  if (marketsRead.error && !markets) {
    return { status: 'error', code: marketsRead.error, noAccount: resolved.noAccount, selectAccount };
  }
  if (!pick) return { status: 'loading', selectAccount };
  if (pick.kind === 'none') {
    return { status: 'no-market', ...resolved, ...(requestedSlug && (lineKind === 'moneyline' || lineKind === 'total') ? { missingMarketSlug: requestedSlug } : {}), selectAccount };
  }
  if (pick.kind === 'ambiguous') return { status: 'ambiguous', count: pick.count, ...resolved, selectAccount };
  return { status: 'ready', market: pick.market, book: bookRead.value, bookError: bookRead.error,
    catalogueUnconfirmed: catalogueType === 'totals' ? marketsRead.error : null, ...resolved, selectAccount };
}
